import React from 'react';
import {
    Box,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { trakrefBlue, red, Grey2, Grey4, Black, DividerColor } from '../../../color_utils';

// Graphical left-to-right chart view of a ruleset. Rules whose expressions
// reference another rule's name branch off to the right of that rule with
// drawn connector lines (see buildReferenceForest in rulesetUtils); children
// stack vertically, so wide fanouts wrap down the page instead of scrolling
// sideways. Rules with no references either way render as a compact card grid
// below the charts. Connectors are pure CSS, so no graph library is needed.

// Connector lines were nearly invisible against the card backgrounds (Grey4 is
// #F5F5F5), which made the grouping hard to read; use a visible cool grey and a
// faint teal band behind each set of children so a parent and its children read
// as one group.
const CONNECTOR_COLOR = '#9AA4B2';
const CONNECTOR = `2px solid ${CONNECTOR_COLOR}`;
const GROUP_BAND = 'rgba(0,130,153,0.05)';
const GROUP_RAIL = 'rgba(0,130,153,0.25)';

// Above this many children, a node's referencing rules render as a wrapping
// grid panel (joined to the parent by a single line) instead of one long
// connector column, so large fanouts wrap within the page.
const WRAP_AT = 5;

const chartSx = {
    '& ul': {
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 0 0 28px',
        padding: '2px 0',
        position: 'relative',
        // shade the block of children so it reads as a group under its parent
        background: GROUP_BAND,
        borderLeft: `3px solid ${GROUP_RAIL}`,
        borderRadius: '0 8px 8px 0',
    },
    // stub from the parent card's center to the sibling rail
    '& ul::before': {
        content: '""',
        position: 'absolute',
        left: '-28px',
        top: '50%',
        width: '28px',
        borderTop: CONNECTOR,
    },
    '& li': {
        position: 'relative',
        padding: '6px 0 6px 28px',
    },
    // elbow into each child card
    '& li::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        width: '28px',
        borderTop: CONNECTOR,
    },
    // vertical rail along the siblings
    '& li::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        borderLeft: CONNECTOR,
    },
    '& li:first-of-type::before': { top: '50%', height: '50%' },
    '& li:last-of-type::before': { top: 0, height: '50%' },
    '& li:only-child::before': { display: 'none' },
    // wrapping panel used instead of a column when a node has many children
    '& .rule-fanout': {
        position: 'relative',
        marginLeft: '28px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '12px',
        border: `1px dashed ${CONNECTOR_COLOR}`,
        borderLeft: `3px solid ${GROUP_RAIL}`,
        borderRadius: '8px',
        background: GROUP_BAND,
        flex: 1,
        minWidth: 0,
    },
    '& .rule-fanout::before': {
        content: '""',
        position: 'absolute',
        left: '-28px',
        top: '50%',
        width: '28px',
        borderTop: CONNECTOR,
    },
};

const NodeCard = ({ node, onEdit, onDelete, isRoot }) => (
    <Tooltip
        arrow
        placement="top"
        componentsProps={{ tooltip: { sx: { maxWidth: 560 } } }}
        title={
            <Box sx={{ p: 0.5 }}>
                {node.expression && (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {node.expression}
                    </Typography>
                )}
                {node.errorMessage && (
                    <Typography sx={{ fontSize: '11px', fontStyle: 'italic', mt: node.expression ? 1 : 0 }}>
                        {node.errorMessage}
                    </Typography>
                )}
                {onEdit && (
                    <Typography sx={{ fontSize: '10px', mt: 1, opacity: 0.8 }}>
                        Click to open
                    </Typography>
                )}
            </Box>
        }
    >
        <Box
            onClick={onEdit ? () => onEdit(node) : undefined}
            sx={{
                cursor: onEdit ? 'pointer' : 'default',
                width: 190,
                flexShrink: 0,
                p: '10px 12px',
                border: `1px solid ${Grey4}`,
                borderLeft: `4px solid ${node.cyclic ? red : (isRoot ? trakrefBlue : Grey4)}`,
                borderRadius: '8px',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                ...(onEdit && {
                    '&:hover': {
                        boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                        borderColor: trakrefBlue,
                    },
                }),
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                <Typography sx={{ flexGrow: 1, fontWeight: 600, fontSize: '13px', color: Black, wordBreak: 'break-word' }}>
                    {node.name}
                </Typography>
                {onDelete && (
                    <Tooltip title="Delete rule">
                        <IconButton
                            size="small"
                            sx={{ p: 0, mt: '-2px' }}
                            onClick={e => {
                                e.stopPropagation();
                                onDelete(node);
                            }}
                        >
                            <DeleteOutlineIcon sx={{ color: Grey2, fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {node.errorMessage && (
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: Grey2,
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {node.errorMessage}
                </Typography>
            )}
            {node.enabled === false && (
                <Typography sx={{ fontSize: '10px', color: Grey2, mt: 0.5, textTransform: 'uppercase' }}>
                    Disabled
                </Typography>
            )}
            {node.cyclic && (
                <Typography sx={{ fontSize: '10px', color: red, mt: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>
                    Reference cycle
                </Typography>
            )}
        </Box>
    </Tooltip>
);

const renderNode = (node, onEdit, onDelete, isRoot) => (
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: node.children.length > WRAP_AT ? 1 : 'none' }}>
        <NodeCard node={node} onEdit={onEdit} onDelete={onDelete} isRoot={isRoot} />
        {node.children.length > 0 && node.children.length <= WRAP_AT && (
            <ul>
                {node.children.map(child => (
                    <li key={child.id}>
                        {renderNode(child, onEdit, onDelete, false)}
                    </li>
                ))}
            </ul>
        )}
        {node.children.length > WRAP_AT && (
            <Box className="rule-fanout">
                {node.children.map(child => (
                    <React.Fragment key={child.id}>
                        {renderNode(child, onEdit, onDelete, false)}
                    </React.Fragment>
                ))}
            </Box>
        )}
    </Box>
);

const RuleTree = ({ tree, onEdit, onDelete }) => {
    const chains = tree.filter(node => node.children.length > 0);
    const standalone = tree.filter(node => node.children.length === 0);

    return (
        <Box>
            {chains.map(chain => (
                <Box
                    key={chain.id}
                    sx={{
                        ...chartSx,
                        overflowX: 'auto',
                        p: 2,
                        mb: 2,
                        border: `1px solid ${DividerColor}`,
                        borderRadius: '8px',
                        backgroundColor: '#fafafa',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}
                >
                    {renderNode(chain, onEdit, onDelete, true)}
                </Box>
            ))}
            {standalone.length > 0 && (
                <>
                    <Typography sx={{ color: Grey2, fontSize: '12px', mb: 1, mt: chains.length > 0 ? 2 : 0 }}>
                        Standalone rules — not referenced by any other rule
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {standalone.map(node => (
                            <NodeCard key={node.id} node={node} onEdit={onEdit} onDelete={onDelete} isRoot={false} />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default RuleTree;
